import { Parser } from '~/api';
import { Node, Tag } from '~/api/models';
import { useModelsStore } from '~/stores';
import RequestService from './request';

interface GetNodeDaemonInfoRequest {
    id: number;
}

interface GetMassNodeDaemonInfoRequest {
    nodes: number[];
}

interface UpdateNodeRequest {
    name: string;
    description?: string;
    public: boolean;
    display_fqdn?: string;
    maintenance_node: boolean;
    location_id: number;

    cpu: number;
    cpu_overallocate?: number;
    memory: number;
    memory_overallocate?: number;
    disk: number;
    disk_overallocate?: number;

    upload_size: number;
    daemonListen: number;
    daemonSFTP: number;
    daemonFastdl: number;

    tags?: string[];
    deployment_tags?: string[];
}

export interface NodeDaemonInfo {
    alive: boolean;
    version: string;
    system: {
        type: string;
        arch: string;
        kernel: string;
        cpus: number;
    };
}

class NodeService {
    getAll(req: PaginatableRequest): Promise<Node[]> {
        return RequestService.get('/nodes', {
            ...req,
            sort: req.sort ?? 'name',
            include: ['location', 'serversCount', 'tags', 'deploymentTags'],
        }).then(Parser.parse);
    }

    get(): Promise<Node> {
        return RequestService.get('/nodes/:node', {
            include: ['location', 'tags', 'deploymentTags'],
        }).then(Parser.parse);
    }

    syncTags(data: { tags: string[] }): Promise<Tag[]> {
        return RequestService.put('/nodes/:node/tags', data)
            .then(Parser.parse)
            .then((tags) => {
                useModelsStore().refreshModel('node');

                return tags;
            });
    }

    syncDeploymentTags(data: { tags: string[] }): Promise<Tag[]> {
        return RequestService.put('/nodes/:node/deployment-tags', data)
            .then(Parser.parse)
            .then((tags) => {
                useModelsStore().refreshModel('node');

                return tags;
            });
    }

    update(data: UpdateNodeRequest): Promise<Node> {
        return RequestService.put('/nodes/:node?include=tags,deploymentTags', data)
            .then(Parser.parse)
            .then((node) => {
                useModelsStore().refreshModel('node');

                return node;
            });
    }

    delete(): Promise<void> {
        return RequestService.delete('/nodes/:node');
    }

    daemonInfo(data: GetNodeDaemonInfoRequest): Promise<NodeDaemonInfo> {
        return RequestService.get(`/nodes/${data.id}/daemon-info`);
    }

    massDaemonInfo(data: GetMassNodeDaemonInfoRequest): Promise<Record<string, NodeDaemonInfo>> {
        return RequestService.get('/nodes/daemon-info', data);
    }
}

export default new NodeService();
